import { useCallback, useEffect, useRef, useState } from 'react';

import { useControlledTimeout } from '../../useControlledTimeout/useControlledTimeout';
import { useEventCallback } from '../../useEventCallback/useEventCallback';
import { fetchHealthCheck } from '../../../api/authApis';

type OfflineReason = 'NO_INTERNET' | 'API_UNREACHABLE';

type UseInternetWatcherProps = {
    onReconnect?: () => void;
    onDisconnect?: () => void;
    start?: boolean;
    ws: React.MutableRefObject<WebSocket | null | undefined>;
};

const POLL_INTERVAL_MS = 3000;
const API_TIMEOUT_MS = 3000;
const PONG_TIMEOUT_MS = 3000;
const PING_INTERVAL_MS = 1000;

export const useInternetWatcher = ({
    onReconnect,
    onDisconnect,
    start = false,
    ws,
}: UseInternetWatcherProps) => {
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [startPolling, setStartPolling] = useState(false);
    const [offlineReason, setOfflineReason] = useState<OfflineReason>();

    const prevOnlineRef = useRef(navigator.onLine);
    const awaitingPong = useRef(false);
    const pingTimeout = useControlledTimeout();
    const pongTimeout = useControlledTimeout();

    const _onReconnect = useEventCallback(onReconnect);
    const _onDisconnect = useEventCallback(onDisconnect);

    const markOnline = useCallback(() => {
        setIsOnline(true);

        if (!prevOnlineRef.current) {
            setStartPolling(false);
            _onReconnect?.();
        }

        prevOnlineRef.current = true;
    }, [_onReconnect]);

    const markOffline = useCallback(() => {
        setIsOnline(false);

        if (prevOnlineRef.current) {
            awaitingPong.current = false;
            pingTimeout.clear();
            pongTimeout.clear();
            setStartPolling(true);
            _onDisconnect?.();
        }

        prevOnlineRef.current = false;
    }, [_onDisconnect]);

    const sendPing = () => {
        try {
            ws.current?.send('ping');

            awaitingPong.current = true;
            pingTimeout.clear();
            pongTimeout.clear();

            pongTimeout.start(() => {
                awaitingPong.current = false;
                setStartPolling(true);
            }, PONG_TIMEOUT_MS);
        } catch {
            awaitingPong.current = false;
            setStartPolling(true);
        }
    };

    const handleMessage = (e: MessageEvent<any>) => {
        if (!awaitingPong.current || e.data !== 'pong') return;

        pongTimeout.clear();

        pingTimeout.start(sendPing, PING_INTERVAL_MS);
    };

    const pollApi = useCallback(async () => {
        try {
            const result = await fetchHealthCheck(API_TIMEOUT_MS);

            const serviceUp = !!result.service;
            const atcUp = !!result.atc;

            if (serviceUp && atcUp) {
                markOnline();
                return;
            }

            throw new Error('One or both services are unreachable');
        } catch {
            markOffline();
            setOfflineReason('NO_INTERNET');
        }
    }, [markOnline, markOffline]);

    useEffect(() => {
        if (start && ws.current?.readyState === WebSocket.OPEN && !startPolling) {
            const websocket = ws.current;

            websocket?.addEventListener('message', handleMessage);
            sendPing();
        }

        return () => {
            ws.current?.removeEventListener('message', handleMessage);

            pingTimeout.clear();
            pongTimeout.clear();
        };
    }, [start, ws.current?.readyState, startPolling]);

    useEffect(() => {
        if (!start || !startPolling) return;

        let isCancelled = false;
        let timerId: ReturnType<typeof setTimeout> | null = null;

        const runPoll = async () => {
            try {
                await pollApi();
            } finally {
                if (!isCancelled) {
                    timerId = setTimeout(runPoll, POLL_INTERVAL_MS);
                }
            }
        };

        void runPoll();

        return () => {
            isCancelled = true;

            if (timerId) {
                clearTimeout(timerId);
            }
        };
    }, [start, startPolling]);

    useEffect(() => {
        if (!start) return;

        const handleOnline = () => {
            void pollApi();
        };

        const handleOffline = () => {
            markOffline();
            setOfflineReason('NO_INTERNET');
        };

        globalThis.addEventListener('online', handleOnline);
        globalThis.addEventListener('offline', handleOffline);

        return () => {
            globalThis.removeEventListener('online', handleOnline);
            globalThis.removeEventListener('offline', handleOffline);
        };
    }, [start]);

    return {
        online: isOnline,
        offlineReason: offlineReason,
    };
};
